import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import {
  ArrowRight,
  QrCode,
  Camera,
  Code2,
  Layers,
  Shield,
  Zap,
  Mail,
  ChevronRight,
} from 'lucide-react';

const products = [
  {
    id: 'cardapio',
    icon: QrCode,
    name: 'app.Cardápio',
    tagline: 'Cardápio digital com QR Code',
    description:
      'Cadastre produtos, gere um QR Code fixo e atualize preços em tempo real. Sem app para o cliente — ele escaneia e vê no navegador.',
    status: 'live',
    link: '/appcardapio',
  },
  {
    id: 'studio',
    icon: Camera,
    name: 'app.Studio',
    tagline: 'Gestão completa para fotógrafos',
    description:
      'Agenda, contratos, orçamentos, álbuns com entrega online, cobranças automatizadas e portal do cliente. Tudo em um só lugar.',
    status: 'soon',
    link: null,
  },
];

const differentials = [
  {
    icon: Layers,
    title: 'Multi-tenant',
    text: 'Cada empresa tem seu ambiente isolado com dados, usuários e configurações independentes.',
  },
  {
    icon: Zap,
    title: 'Rápido e leve',
    text: 'Infraestrutura serverless na AWS. Escalabilidade automática, sem servidor para gerenciar.',
  },
  {
    icon: Shield,
    title: 'Seguro',
    text: 'Autenticação via Amazon Cognito, dados criptografados e backups diários.',
  },
  {
    icon: Code2,
    title: 'Evolução contínua',
    text: 'Novas funcionalidades e melhorias entregues constantemente via deploy automatizado.',
  },
];

export default function HomePage() {
  return (
    <div className="min-h-screen overflow-x-hidden bg-[#fdfbf7] text-[#2b211a]">
      <Seo
        title="Soluções digitais para empresas"
        description="Plataforma SaaS com soluções prontas para diferentes segmentos: cardápio digital com QR Code, gestão para fotógrafos e mais. Você foca no negócio, a tecnologia fica com a gente."
        path="/"
      />
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-[#e9dfd5] bg-[#fdfbf7]/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <div className="flex items-center gap-5">
            <Link to="/" className="flex items-center gap-3" aria-label="Bloise Tech — início">
              <img src="/logo.png" alt="Bloise Tech" className="h-9" />
            </Link>
            <a
              href="#produtos"
              className="text-sm font-medium text-[#79685d] transition hover:text-[#2b211a]"
            >
              Produtos
            </a>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="https://wa.me/5511994715161"
              target="_blank"
              rel="noopener noreferrer"
              className="grid h-9 w-9 place-items-center rounded-lg text-[#25D366] transition hover:bg-[#f0f9f4]"
              aria-label="WhatsApp"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            </a>
            <a
              href="https://instagram.com/bloisetech"
              target="_blank"
              rel="noopener noreferrer"
              className="grid h-9 w-9 place-items-center rounded-lg text-[#E4405F] transition hover:bg-[#fdf0f3]"
              aria-label="Instagram @bloisetech"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
            </a>
            <a
              href="#contato"
              className="rounded-lg bg-[#FC5301] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#d94701] active:scale-[0.98]"
            >
              Contato
            </a>
          </div>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="mx-auto max-w-6xl px-5 py-20 text-center md:py-28">
            <span className="inline-flex items-center gap-2 rounded-full bg-[#f9e7d5] px-3 py-1 text-xs font-semibold text-[#9f3309]">
              Tecnologia para o seu negócio
            </span>
            <h1 className="mx-auto mt-6 max-w-3xl text-4xl font-black leading-[1.1] tracking-tight sm:text-5xl md:text-6xl">
              Sistemas que{' '}
              <span className="relative whitespace-nowrap text-[#FC5301]">
                simplificam
                <svg
                  className="absolute -bottom-2 left-0 w-full"
                  height="12"
                  viewBox="0 0 300 12"
                  fill="none"
                  preserveAspectRatio="none"
                  aria-hidden="true"
                >
                  <path d="M2 9C60 3 220 2 298 7" stroke="#FC5301" strokeWidth="4" strokeLinecap="round" />
                </svg>
              </span>{' '}
              a gestão da sua empresa
            </h1>
            <p className="mx-auto mt-6 max-w-xl text-lg text-[#79685d]">
              Plataforma SaaS com soluções prontas para diferentes segmentos. Você foca no seu negócio, a tecnologia fica com a gente.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <a
                href="#produtos"
                className="inline-flex items-center gap-2 rounded-lg bg-[#FC5301] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#d94701] active:scale-[0.98]"
              >
                Conhecer produtos <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="#contato"
                className="inline-flex items-center gap-2 rounded-lg border border-[#dfd2c7] bg-white px-6 py-3 text-sm font-semibold transition hover:bg-[#f5eee7] active:scale-[0.98]"
              >
                Falar conosco
              </a>
            </div>
          </div>
        </section>

        {/* Products */}
        <section id="produtos" className="scroll-mt-20 border-t border-[#e9dfd5] bg-[#f6efe8]">
          <div className="mx-auto max-w-6xl px-5 py-16 md:py-20">
            <div className="max-w-xl">
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Nossos Produtos</h2>
              <p className="mt-3 text-[#79685d]">
                Soluções digitais desenvolvidas para resolver problemas reais do dia a dia empresarial.
              </p>
            </div>
            <div className="mt-10 grid gap-6 md:grid-cols-2">
              {products.map((product) => {
                const Icon = product.icon;
                return (
                  <article
                    key={product.id}
                    className="group relative overflow-hidden rounded-2xl border border-[#e5d8ce] bg-white p-8 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                  >
                    {product.status === 'soon' && (
                      <span className="absolute right-4 top-4 rounded-full bg-[#f9e7d5] px-3 py-1 text-xs font-semibold text-[#9f3309]">
                        Em breve
                      </span>
                    )}
                    <span className="grid h-12 w-12 place-items-center rounded-xl bg-[#f9e7d5] text-[#a7370a]">
                      <Icon className="h-6 w-6" strokeWidth={2} />
                    </span>
                    <h3 className="mt-5 text-xl font-bold">{product.name}</h3>
                    <p className="mt-1 text-sm font-medium text-[#FC5301]">{product.tagline}</p>
                    <p className="mt-3 text-sm leading-relaxed text-[#79685d]">{product.description}</p>
                    {product.link && (
                      <Link
                        to={product.link}
                        className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[#FC5301] transition hover:gap-2"
                      >
                        Saiba mais <ChevronRight className="h-4 w-4" />
                      </Link>
                    )}
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* Differentials */}
        <section className="mx-auto max-w-6xl px-5 py-16 md:py-20">
          <div className="max-w-xl">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Por que a Bloise Tech?</h2>
            <p className="mt-3 text-[#79685d]">
              Infraestrutura moderna, segura e escalável para empresas de qualquer tamanho.
            </p>
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {differentials.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="rounded-2xl border border-[#e5d8ce] bg-white p-6 shadow-sm">
                  <span className="grid h-11 w-11 place-items-center rounded-xl bg-[#f9e7d5] text-[#a7370a]">
                    <Icon className="h-5 w-5" strokeWidth={2} />
                  </span>
                  <h3 className="mt-4 text-lg font-semibold">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#79685d]">{item.text}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Contact / CTA */}
        <section id="contato" className="scroll-mt-20 border-t border-[#d94701] bg-[#FC5301] text-white">
          <div className="mx-auto flex max-w-6xl flex-col items-center gap-6 px-5 py-16 text-center">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Vamos conversar?</h2>
            <p className="max-w-lg text-white/80">
              Quer saber mais sobre nossas soluções ou tem um projeto em mente? Entre em contato.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <a
                href="https://wa.me/5511994715161"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg bg-[#fdfbf7] px-7 py-3 text-sm font-semibold text-[#2b211a] transition hover:bg-white active:scale-[0.98]"
              >
                <svg className="h-4 w-4 text-[#25D366]" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                WhatsApp
              </a>
              <a
                href="mailto:contato@bloise.com.br"
                className="inline-flex items-center gap-2 rounded-lg bg-[#fdfbf7] px-7 py-3 text-sm font-semibold text-[#2b211a] transition hover:bg-white active:scale-[0.98]"
              >
                <Mail className="h-4 w-4" /> contato@bloise.com.br
              </a>
              <a
                href="https://instagram.com/bloisetech"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg bg-[#fdfbf7] px-7 py-3 text-sm font-semibold text-[#2b211a] transition hover:bg-white active:scale-[0.98]"
              >
                <svg className="h-4 w-4 text-[#E4405F]" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                @bloisetech
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#e9dfd5] bg-[#fdfbf7]">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-5 py-8 text-sm text-[#79685d] sm:flex-row">
          <img src="/logo.png" alt="Bloise Tech" className="h-7" />
          <span>© {new Date().getFullYear()} Bloise Tech — Todos os direitos reservados</span>
        </div>
      </footer>
    </div>
  );
}
